import {
  FieldValues,
  useController,
  UseControllerProps,
} from 'react-hook-form';
import { TextField, TextFieldProps } from '@stitch-fix/mode-react';
import { G, S } from '@mobily/ts-belt';

interface ControlledTextFieldProps<TFieldValues extends FieldValues> {
  /**
   * sources:
   *
   * - https://www.chromium.org/developers/design-documents/create-amazing-password-forms/
   * - https://html.spec.whatwg.org/multipage/form-control-infrastructure.html#autofilling-form-controls%3A-the-autocomplete-attribute
   * - https://www.chromium.org/developers/design-documents/form-styles-that-chromium-understands/
   */
  autocomplete?: HTMLInputElement['autocomplete'];
  control: UseControllerProps<TFieldValues>['control'];
  name: UseControllerProps<TFieldValues>['name'];
  label: TextFieldProps['label'];
  helpText?: TextFieldProps['helpText'];
  type?: HTMLInputElement['type'];
  rows?: TextFieldProps['rows'];
}

/**
 * This component allows [React Hook Form](https://react-hook-form.com/)
 * to easily communicate with Mode React's [TextField](https://mode-react.daylight.stitchfix.com/?path=/docs/components-text-field--example)
 */
export const ControlledTextField = <TFieldValues extends FieldValues>({
  autocomplete,
  control,
  label,
  name,
  helpText,
  type,
  rows,
}: ControlledTextFieldProps<TFieldValues>) => {
  const {
    field: { name: textFieldName, onChange, value },
    fieldState: { error },
  } = useController({ control, name });

  const hasError =
    G.isObject(error) &&
    G.isString(error.message) &&
    S.isNotEmpty(error.message);

  return (
    <TextField
      autoComplete={autocomplete}
      helpText={hasError && (helpText || error.message)}
      name={textFieldName}
      label={label}
      value={value || ''}
      onChange={onChange}
      state={hasError ? 'error' : undefined}
      mb={1}
      type={type}
      rows={rows}
    />
  );
};
